<template>
  <div>
    <b-row>
      <b-form-group
        :id="filterLabelId"
        :label="labelWithoutColon"
        :label-for="filterId"
        :class="labelClass"
      >
        <template #default>
          <div v-if="hasHovertip">
            &nbsp;
            <img
              v-if="hasHovertip"
              :id="getHovertipId"
              :src="hovertipSrc"
              alt="help"
            />
            <b-popover
              v-if="hasHovertip"
              :target="getHovertipId"
              triggers="hover focus"
            >
              <template #title>
                {{ getHovertipTitle }}
              </template>
              {{ getHovertipText }}
            </b-popover>
          </div>
        </template>
      </b-form-group>
      <b-form-select
        v-model="selected"
        v-if="!hideEmpty || getValue.length"
        :show-labels="false"
        :id="filterId"
        :class="filterClass"
        :disabled="disable"
        :value-field="trackBy"
        :text-field="textField"
        :value="$store.getters[storeWatch]"
        :options="getValue"
        :label="displayValue"
        :multiple="multiple"
        @change="updateFilter"
        :required="elementRequired"
      >
        <!-- This slot appears above the options from 'options' prop -->
        <template slot="first">
          <b-form-select-option :value="null" :disabled="disableEmptySelection">
            -- Please select an option --
          </b-form-select-option>
        </template>
      </b-form-select>
    </b-row>
  </div>
</template>

<script>
import { isPlainObject } from "lodash";
import PathMapper from "../../lib/PathMapper";
import jsonApiFilterManager from "../../lib/JsonApiFilterManager";
import hoverTip from "../raft/help-Outline.svg";

export default {
  name: "SingleSelectFilter",
  data() {
    return {
      hovertipSrc: hoverTip,
      selected: null,
    };
  },
  methods: {
    updateFilter(value) {
      if (this.storeCommit && this.storeCommit.length > 1) {
        this.$store.commit(this.storeCommit, value);
      } else {
        if (this.updateMethod) {
          this.updateMethod(value, this.path);
        }
        this.selected = value;
      }
      // eslint-disable-next-line valid-jsdoc
      /**
       * @param {Object|Object[]|null} options - potential array
       * @return {options is Object[]}
       */
      function isArrayOfTrackedObjects(options, trackBy) {
        return (
          Array.isArray(options) &&
          isPlainObject(options[0]) &&
          options[0].hasOwnProperty(trackBy)
        );
      }
      let itemsSelected = [];
      if (isArrayOfTrackedObjects(this.getValue, this.trackBy)) {
        itemsSelected = this.getValue.filter((option) =>
          value.includes(option[this.trackBy]),
        );
      }
      this.$emit("update-filter", {
        id: this.id,
        path: this.path,
        value,
        filterAction: this.filterAction,
        filterKey: this.filterKey,
        trackBy: this.trackBy,
        itemsSelected,
      });
    },
    clearSelected: function () {
      this.selected = null;
      this.$emit(this.id + "-clear", {});
    },
  },
  computed: {
    filterId: function () {
      return this.id;
    },
    filterLabelId: function () {
      return this.id + "-label";
    },
    labelWithoutColon: function () {
      if (!this.hideEmpty || this.getValue.length) {
        return this.label;
      }
      return "";
    },
    labelClass: function () {
      const value = [];
      if (this.disable) {
        value.push("multiselect-label--disabled");
      }
      if (this.isRequired) {
        value.push("form-required");
      }
      return value.join(" ");
    },
    elementRequired: function () {
      return this.isRequired;
    },
    getHelpIconPath: function () {
      return PathMapper.getIconAssetPath("help.png", this.$isB4);
    },
    hasHovertip: function () {
      return this.hovertip;
    },
    getHovertipId: function () {
      return this.hovertip?.id;
    },
    getHovertipTitle: function () {
      return this.hovertip?.title;
    },
    getHovertipText: function () {
      return this.hovertip?.text;
    },
  },
  props: {
    id: { type: String, required: true },
    label: { type: String, required: true },
    updateMethod: { type: Function, default: null },
    selectedItems: { type: [Array, Object, String], default: "" },
    /** @type {object|object[]|null} */
    getValue: { type: [Object, Array], default: null },
    trackBy: { type: String, default: "value" },
    filterAction: { type: String, default: null },
    filterKey: { type: String, default: null },
    disable: Boolean,
    disableEmptySelection: { type: Boolean, default: true },
    multiple: { type: Boolean, default: true },
    hideEmpty: { type: Boolean, default: false },
    displayValue: { type: String, default: "" },
    filterClass: { type: Array, default: null },
    storeWatch: { type: String, default: null },
    storeCommit: { type: String, default: null },
    path: { type: String, default: null },
    operator: { type: String, default: "=" },
    isRequired: { type: Boolean, default: null },
    hovertip: { type: Object, default: null },
    textField: { type: String, default: "title" },
  },
  mounted() {
    this.$store.watch(
      (state, getters) => this.$store.getters[this.storeWatch],
      (newValue) => {
        if (
          typeof newValue === "undefined" ||
          newValue === null ||
          newValue.length === 0
        ) {
          jsonApiFilterManager.deleteFiltersByName([this.id]);
          this.selected = "null";
          return;
        }
        if (typeof newValue === "object" && !Array.isArray(newValue)) {
          newValue = [newValue];
        }
        const options = [];
        for (let i = 0, len = newValue.length; i < len; i++) {
          options.push(newValue[i][this.trackBy]);
        }
        jsonApiFilterManager.deleteFiltersByName([this.id]);
        if (options.length > 0 && this.path) {
          jsonApiFilterManager.addMultivalueNamedFilter(
            this.id,
            this.path,
            options,
            this.storeCommit,
            newValue,
          );
        }
      },
    );
    if (
      !this.storeWatch &&
      this.selectedItems &&
      this.selectedItems.length === 1
    ) {
      this.selected = this.selectedItems[0];
    }
  },
};
</script>

<style scoped></style>
